howspace.workspace.aiBuilder = (() => {
    'use strict';

    // Creating a workspace is not idempotent and nothing caps how many unfinished plans a user may have, so
    // a double click must not produce two workspaces.
    let aiBuilderRequestInFlight = false;

    // How long the modal waits for workspace:ready before it stops claiming to be busy. Generous, because
    // the wait is a worker round trip on a shared queue and finishing late is normal.
    const AI_BUILDER_CREATE_TIMEOUT_MS = 60000;

    // A floor, not a delay. A notification that lands almost at once would flash the creating state and
    // leave, which reads as a glitch rather than as progress, so it stays up long enough to be read. Once it
    // has been up that long this costs nothing, which a flat pause would not.
    const AI_BUILDER_CREATE_MIN_VISIBLE_MS = 800;

    let aiBuilderSlowTimer = 0;
    let aiBuilderCreatingShownAt = 0;

    // Refused because the device has not been PIN validated, which only My Howspace asks for. Expected rather
    // than broken, so it is neither reported to the user as an error nor logged as one.
    const isUnknownDeviceResponse = response => {
        return response?.responseJSON?.errors?.[0]?.error?.key === 'device_not_known';
    };

    /**
     * The hand off, as a named module level handler so there is only ever one of it.
     *
     * It used to be an inline closure registered per attempt and never removed. A failed create resets the
     * in-flight flag but left its handler behind, so the next attempt added a second one, and either would
     * then fire on any later workspace:ready in the tab, including one for a workspace created somewhere
     * else entirely, and navigate away from whatever the user was doing.
     */
    const onAiBuilderWorkspaceReady = (notification) => {
        window.clearTimeout(aiBuilderSlowTimer);
        real.events.off('workspace:ready', onAiBuilderWorkspaceReady);

        const url = notification?.object?.loginlink?.url;
        if (!url) {
            return;
        }

        const shownFor = Date.now() - aiBuilderCreatingShownAt;
        window.setTimeout(() => {
            window.location = url;
        }, Math.max(0, AI_BUILDER_CREATE_MIN_VISIBLE_MS - shownFor));
    };

    /**
     * Turns the modal into the creating state: the wait has no upper bound, so a spinner on the tile is not
     * enough of a signal. Everything the modal was showing is hidden rather than replaced, so a failed
     * request can put it straight back.
     */
    const showAiBuilderCreating = (modal) => {
        const creating = modal.find('.ai-builder-creating');
        if (creating.length === 0) {
            return null;
        }

        // The selection modal hides its own footer on init, so what the footer was doing before this is
        // remembered rather than assumed. Restoring it wrongly would reveal buttons that modal never shows.
        const footer = modal.find('.modal-footer');
        creating.data('footer-was-hidden', footer.hasClass('d-none'));

        aiBuilderCreatingSiblings(modal, creating).addClass('d-none');
        footer.addClass('d-none');
        creating.removeClass('d-none');

        return creating;
    };

    const hideAiBuilderCreating = (modal, creating) => {
        aiBuilderCreatingSiblings(modal, creating).removeClass('d-none');
        creating.addClass('d-none');
        if (creating.data('footer-was-hidden') !== true) {
            modal.find('.modal-footer').removeClass('d-none');
        }
    };

    // Everything the creating state stands in front of. In a modal that is the body, on the landing page
    // hero it is the section itself, so the scope follows whichever holds it rather than being assumed.
    const aiBuilderCreatingSiblings = (container, creating) => {
        const body = container.find('.modal-body');

        return (body.length > 0 ? body : container).children().not(creating);
    };

    /**
     * Creates the workspace in building state and hands off to the workspace itself. The redirect happens on
     * the workspace:ready notification, which carries a single use login link for the new workspace.
     *
     * That notification is also why the request succeeding is not the end of it: the create call answers 200
     * as soon as the workspace row exists, so its error handler can never report a hand off that never
     * arrives. The timeout is what covers that, and it says the workspace may still be coming rather than
     * that it failed, because it may well be.
     */
    const create = (modal, btn, customerId = null, goal = '') => {
        if (aiBuilderRequestInFlight) {
            return;
        }
        aiBuilderRequestInFlight = true;

        const creating = showAiBuilderCreating(modal);
        if (creating === null) {
            real.button.loading(btn, modal);
        }
        aiBuilderCreatingShownAt = Date.now();

        // Once the modal has told them to go and look for the workspace themselves, the hand off stops
        // listening: a notification arriving minutes later would otherwise navigate them away from whatever
        // they had moved on to. The in-flight guard deliberately stays set, because the answer to a create
        // that may already have worked is never to start a second one.
        aiBuilderSlowTimer = window.setTimeout(() => {
            real.events.off('workspace:ready', onAiBuilderWorkspaceReady);
            creating?.find('.ai-builder-creating-busy').addClass('d-none');
            creating?.find('.ai-builder-creating-slow').removeClass('d-none');
        }, AI_BUILDER_CREATE_TIMEOUT_MS);

        // Off before on, so a handler that somehow outlived its attempt cannot be joined by a second.
        real.events.off('workspace:ready', onAiBuilderWorkspaceReady);
        real.events.on('workspace:ready', onAiBuilderWorkspaceReady);

        real.ajax.json(`${real.baseUri}/api/ai-builder/create`, {
            type: 'POST',
            data: JSON.stringify({
                ...(customerId ? {customer: customerId} : {}),
                ...(goal ? {goal} : {}),
            }),
            showErrorNotification: response => !isUnknownDeviceResponse(response),
            logError: response => !isUnknownDeviceResponse(response),
            error: response => {
                window.clearTimeout(aiBuilderSlowTimer);
                real.events.off('workspace:ready', onAiBuilderWorkspaceReady);
                aiBuilderRequestInFlight = false;
                if (creating === null) {
                    real.button.reset(btn, modal);
                } else {
                    hideAiBuilderCreating(modal, creating);
                }

                // The device stopped being known while the modal was open. Same answer as clicking the tile
                // on an unknown device: validate, then start again.
                if (isUnknownDeviceResponse(response)) {
                    howspace.components.modal.show(`${real.baseUri}/device/verify-modal`);
                }
            }
        });
    };

    // The slow state cannot tell whether the workspace exists, so the one thing it offers is a fresh look.
    real.dom.$body.on('click', '.ai-builder-creating-reload-btn', () => {
        window.location.reload();
    });

    return {
        create: create,
    };
})();
